"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import {
  ArrowLeft,
  Calendar,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Globe,
  Gauge,
  Hash,
  AtSign,
  MapPin,
  Newspaper,
  Ruler,
  Trophy,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { volledigeDatum, datumInJaar } from "@/lib/format";
import { FavorietKnop } from "@/components/favoriet-knop";
import { DeelKnop } from "@/components/deel-knop";
import { NieuwsFoto } from "@/components/nieuws-foto";
import type { TeamDetail, TeamSeizoen, TeamResultaat } from "@/server/team";

type Tab = "uitslagen" | "stand" | "kalender" | "nieuws" | "team";

const TABS: { id: Tab; label: string }[] = [
  { id: "uitslagen", label: "Uitslagen" },
  { id: "stand", label: "Stand" },
  { id: "kalender", label: "Kalender" },
  { id: "nieuws", label: "Nieuws" },
  { id: "team", label: "Team" },
];

export function TeamProfiel({ team }: { team: TeamDetail }) {
  const [tab, setTab] = useState<Tab>("uitslagen");

  const initialen = team.tractor
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const naam = team.tractor || team.chauffeur || "Onbekend";

  // Het inschrijfnummer naast de standplaats: het vaste nummer van de machine.
  // Niet het startnummer, want dat is de startvolgorde binnen één wedstrijd en
  // verschilt dus per keer; dát staat per uitslagregel.
  const cellen = [
    team.merk ? { icon: Wrench, label: "Merk / type", waarde: team.merk } : null,
    team.woonplaats ? { icon: MapPin, label: "Standplaats", waarde: team.woonplaats } : null,
    team.inschrijfNr != null
      ? { icon: Hash, label: "Inschrijfnr.", waarde: String(team.inschrijfNr) }
      : null,
  ].filter((c): c is NonNullable<typeof c> => c !== null);

  return (
    <div>
      <section className="bg-card text-white">
        <Hero team={team} initialen={initialen} naam={naam} />

        {cellen.length > 0 && (
          <dl
            className="grid gap-px border-t border-white/10 bg-white/10 text-sm"
            style={{ gridTemplateColumns: `repeat(${cellen.length}, minmax(0, 1fr))` }}
          >
            {cellen.map((c) => (
              <div key={c.label} className="bg-card px-4 py-3">
                <div className="flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-white/50">
                  <c.icon className="size-3.5 text-primary" aria-hidden />
                  {c.label}
                </div>
                <p className="mt-0.5 truncate font-semibold text-white">{c.waarde}</p>
              </div>
            ))}
          </dl>
        )}
      </section>

      {/* Tabbladen. Vijf stuks passen niet naast elkaar op een telefoon, dus
          de rij schuift opzij - net als de sectiekeuze op het klassement. */}
      <div
        role="tablist"
        aria-label="Onderdelen"
        className="flex gap-2 overflow-x-auto border-b border-white/10 px-4 py-3"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`paneel-${t.id}`}
            onClick={() => setTab(t.id)}
            className={cn(
              "shrink-0 rounded-full px-4 py-1.5 font-display text-xs font-black uppercase tracking-wide transition-colors",
              tab === t.id ? "bg-primary text-primary-foreground" : "bg-card text-white/70 hover:text-white",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-4 py-5">
        <Paneel id="uitslagen" actief={tab}>
          <Uitslagen team={team} />
        </Paneel>
        <Paneel id="stand" actief={tab}>
          <Stand team={team} />
        </Paneel>
        <Paneel id="kalender" actief={tab}>
          <Kalender team={team} />
        </Paneel>
        <Paneel id="nieuws" actief={tab}>
          <Nieuws team={team} />
        </Paneel>
        <Paneel id="team" actief={tab}>
          <TeamInfo team={team} />
        </Paneel>
      </div>
    </div>
  );
}

// De kop: de machine schermbreed, met de naam eroverheen.
//
// Een tractorpullingmachine is het onderwerp van de pagina, niet een bijschrift
// bij een naam. Een duimnagel van 80 pixels doet dat tekort - vandaar de foto
// over de volle breedte, met een verloop eroverheen zodat de tekst leesbaar
// blijft op elke foto.
//
// Zonder foto blijft het blok even hoog, met de initialen groot op de
// achtergrond. Dat is bij Farm Stock de normale toestand (167 van de 1.135
// machines heeft een foto), dus die staat mag er niet uitzien als een fout.
function Hero({
  team,
  initialen,
  naam,
}: {
  team: TeamDetail;
  initialen: string;
  naam: string;
}) {
  const router = useRouter();
  const [fout, setFout] = useState(false);
  const [vergroot, setVergroot] = useState(false);
  const heeftFoto = !!team.foto && !fout;

  useEffect(() => {
    if (!vergroot) return;
    const sluit = (e: KeyboardEvent) => {
      if (e.key === "Escape") setVergroot(false);
    };
    window.addEventListener("keydown", sluit);
    return () => window.removeEventListener("keydown", sluit);
  }, [vergroot]);

  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
      {heeftFoto ? (
        <button
          type="button"
          onClick={() => setVergroot(true)}
          aria-label="Foto vergroten"
          className="absolute inset-0 block"
        >
          <Image
            src={team.foto!}
            alt={team.tractor}
            fill
            sizes="448px"
            priority
            onError={() => setFout(true)}
            className="object-cover"
          />
        </button>
      ) : (
        <span
          className="absolute inset-0 flex items-center justify-center font-display text-[8rem] font-black leading-none text-primary/10"
          aria-hidden
        >
          {initialen || "?"}
        </span>
      )}

      {/* Verloop van onder naar boven; de tekst staat erin, niet op de foto. */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-muted via-muted/70 to-muted/10"
        aria-hidden
      />

      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-4">
        {/* Terug naar waar je vandaan kwam, niet altijd naar de homepage: wie
            vanaf het live-scherm een deelnemer aantikte, belandde na "Terug"
            op home (Staphorst, 2 oktober). Is er geen vorige pagina - de
            link kwam uit WhatsApp - dan wel naar home. */}
        <Link
          href="/"
          onClick={(e) => {
            if (window.history.length > 1) {
              e.preventDefault();
              router.back();
            }
          }}
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-black/40 px-3 py-1.5 text-sm font-bold text-white backdrop-blur-sm transition-colors hover:text-primary"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Terug
        </Link>
        <span className="pointer-events-auto flex gap-2">
          <DeelKnop titel={naam} tekst={team.klasse ?? undefined} groot />
          <FavorietKnop
            favoriet={{ soort: "team", sectie: team.sectie, id: team.deelnID, naam }}
            groot
          />
        </span>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-4">
        {team.klasse && (
          <p className="font-display text-xs font-black uppercase tracking-[0.2em] text-primary">
            {team.klasse}
          </p>
        )}
        <h1 className="mt-1 kop-groot text-5xl uppercase leading-[0.92] text-white drop-shadow-lg">
          {team.tractor || "Onbekend"}
        </h1>
        {team.chauffeur && (
          <p className="mt-1.5 text-base font-semibold italic text-white/85">{team.chauffeur}</p>
        )}
      </div>

      {vergroot && heeftFoto && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={team.tractor}
          onClick={() => setVergroot(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
        >
          <button
            type="button"
            aria-label="Sluiten"
            className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X className="size-6" aria-hidden />
          </button>
          {/* Bewust een gewone img: met next/image en `fill` beslaat het element
              altijd het hele scherm, en dan sluit een tik naast de foto niet. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={team.foto!}
            alt={team.tractor}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        </div>
      )}
    </div>
  );
}

function Paneel({ id, actief, children }: { id: Tab; actief: Tab; children: React.ReactNode }) {
  return (
    <div role="tabpanel" id={`paneel-${id}`} aria-labelledby={`tab-${id}`} hidden={actief !== id}>
      {children}
    </div>
  );
}

function Leeg({ tekst }: { tekst: string }) {
  return <p className="py-8 text-center text-sm text-muted-foreground">{tekst}</p>;
}

// --- Uitslagen -----------------------------------------------------------
//
// Het lopende seizoen staat open, de rest ingeklapt. Een team dat twintig jaar
// meerijdt heeft tot 387 uitslagen; die onder elkaar is geen pagina meer. Per
// seizoen één regel met de eindstand erbij is de samenvatting die je zoekt, en
// wie meer wil klapt hem open.
function Uitslagen({ team }: { team: TeamDetail }) {
  const [open, setOpen] = useState<number | null>(team.seizoenen[0]?.jaar ?? null);

  if (team.seizoenen.length === 0) {
    return <Leeg tekst="Nog geen uitslagen bekend voor dit team." />;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {team.aantalWedstrijden} wedstrijden in {team.seizoenen.length}{" "}
        {team.seizoenen.length === 1 ? "seizoen" : "seizoenen"}
      </p>
      {team.seizoenen.map((s) => (
        <Seizoen key={s.jaar} seizoen={s} open={open === s.jaar} onWissel={() => setOpen(open === s.jaar ? null : s.jaar)} />
      ))}
    </div>
  );
}

function Seizoen({
  seizoen,
  open,
  onWissel,
}: {
  seizoen: TeamSeizoen;
  open: boolean;
  onWissel: () => void;
}) {
  const gereden = seizoen.resultaten.filter((r) => r.positie != null).length;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={onWissel}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0">
          <span className="flex items-baseline gap-2">
            <span className="font-display text-lg font-black tabular-nums text-white">
              {seizoen.jaar}
            </span>
            {seizoen.eindpositie != null && (
              <span className="font-display text-sm font-black uppercase tracking-wide text-primary">
                {seizoen.eindpositie}e
                {seizoen.aantalInKlasse ? ` van ${seizoen.aantalInKlasse}` : ""}
              </span>
            )}
          </span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {seizoen.klasseNaam ?? "Klasse onbekend"}
            {seizoen.punten != null ? ` · ${seizoen.punten} punten` : ""}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2.5 pt-1">
          <span className="text-xs text-muted-foreground">
            {seizoen.resultaten.length}
            {gereden < seizoen.resultaten.length ? ` · ${gereden} gereden` : ""}
          </span>
          <ChevronDown
            className={cn(
              "size-5 text-primary transition-transform duration-200",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </span>
      </button>

      {open && (
        <ul className="divide-y divide-white/5 border-t border-white/5">
          {seizoen.resultaten.map((r, i) => (
            <ResultaatRij key={i} r={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ResultaatRij({ r }: { r: TeamResultaat }) {
  const gereden = r.positie != null || r.slot != null || r.kwalificatie != null;
  return (
    <li>
      <Link
        href={`/wedstrijd/${r.wedstrijdId}`}
        className="flex items-center gap-3 px-3 py-2.5 transition-colors active:bg-white/5"
      >
        <span className="flex size-10 shrink-0 flex-col items-center justify-center rounded-lg bg-muted">
          {r.positie != null ? (
            <>
              <span className="font-display text-base font-black leading-none tabular-nums text-primary">
                {r.positie}
              </span>
              <span className="text-[0.55rem] font-bold uppercase text-white/50">plek</span>
            </>
          ) : (
            <Trophy className="size-4 text-white/30" aria-hidden />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-white">{r.wedstrijd}</span>
          <span className="block truncate text-xs capitalize text-muted-foreground">
            {volledigeDatum(r.datum)}
            {r.startNR != null ? ` · start ${r.startNR}` : ""}
          </span>
        </span>
        {gereden ? (
          // Kwalificatie én finale, net als op de uitslagpagina. Alleen de
          // beslissende rit tonen laat de helft weg: in Lochem kwalificeerde
          // Snoopy zich op 115,65 m en won hij de finale met 105,97 m.
          <span className="shrink-0 text-right">
            {r.slot && (
              <span className="flex items-center justify-end gap-1 text-sm font-bold text-white">
                <Ruler className="size-3.5 text-primary" aria-hidden />
                {r.slot}
              </span>
            )}
            {r.kwalificatie && (
              <span className="block text-xs text-muted-foreground">
                {r.slot ? "kwal. " : ""}
                {r.kwalificatie}
              </span>
            )}
            {r.toelichting && (
              <span className="block text-[0.65rem] text-muted-foreground">{r.toelichting}</span>
            )}
          </span>
        ) : (
          <span className="shrink-0 text-xs font-medium text-white/40">nog te rijden</span>
        )}
      </Link>
    </li>
  );
}

// --- Stand ---------------------------------------------------------------

function Stand({ team }: { team: TeamDetail }) {
  if (!team.stand) {
    return <Leeg tekst="Dit team heeft dit seizoen nog geen punten in de competitie." />;
  }
  const { jaar, klasseNaam, rijen } = team.stand;

  return (
    <div>
      <p className="mb-3 text-xs text-muted-foreground">
        {klasseNaam} · seizoen {jaar} · {rijen.length} rijders
      </p>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center gap-3 border-b border-border px-3 py-2 text-[0.65rem] font-bold uppercase tracking-wide text-primary">
          <span className="w-6 shrink-0">Pos</span>
          <span className="min-w-0 flex-1">Rijder</span>
          <span className="shrink-0 text-right">Punten</span>
        </div>
        <ul>
          {rijen.map((r) => (
            <li
              key={r.positie}
              className={cn(
                "border-b border-border last:border-b-0",
                r.uitgelicht && "bg-primary/10",
              )}
            >
              <Link
                href={r.href ?? "#"}
                className="flex items-center gap-3 px-3 py-2.5 transition-colors active:bg-white/5"
              >
                <span className="w-6 shrink-0 font-display text-sm font-black tabular-nums text-white">
                  {r.positie}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-white">{r.titel}</span>
                  {r.ondertitel && (
                    <span className="block truncate text-xs text-muted-foreground">{r.ondertitel}</span>
                  )}
                </span>
                <span className="shrink-0 text-right font-mono text-sm tabular-nums text-soft-foreground">
                  {r.punten}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// --- Kalender ------------------------------------------------------------

function Kalender({ team }: { team: TeamDetail }) {
  if (team.agenda.length === 0) {
    return <Leeg tekst="Dit team staat nog nergens ingeschreven." />;
  }
  return (
    <ul className="space-y-2">
      {team.agenda.map((w) => (
        <li key={w.wedstrijdId}>
          <Link
            href={`/wedstrijd/${w.wedstrijdId}`}
            className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 transition-colors active:bg-white/5"
          >
            <span className="flex size-11 shrink-0 flex-col items-center justify-center rounded-lg bg-muted">
              <Calendar className="size-4 text-primary" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-white">{w.naam}</span>
              <span className="block truncate text-xs text-muted-foreground">
                <span className="font-semibold capitalize text-primary">
                  {datumInJaar(w.datum)}
                </span>
                {w.klasseNaam ? ` · ${w.klasseNaam}` : ""}
              </span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-white/30" aria-hidden />
          </Link>
        </li>
      ))}
    </ul>
  );
}

// --- Nieuws --------------------------------------------------------------

function Nieuws({ team }: { team: TeamDetail }) {
  if (team.nieuws.length === 0) {
    return (
      <div className="py-8 text-center">
        <Newspaper className="mx-auto mb-3 size-7 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">
          Nog geen berichten bij de wedstrijden waar dit team reed.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-3 text-xs text-muted-foreground">
        Berichten bij de wedstrijden waar dit team reed
      </p>
      <ul className="space-y-3">
        {team.nieuws.map((n) => (
          <li key={n.id}>
            <Link
              href={`/nieuws/${n.id}`}
              className="flex gap-3 overflow-hidden rounded-xl border border-border bg-card transition-colors active:bg-white/5"
            >
              <span className="relative h-20 w-28 shrink-0 overflow-hidden bg-muted">
                <NieuwsFoto src={n.foto} alt={n.titel} />
              </span>
              <span className="min-w-0 flex-1 py-2.5 pr-3">
                <span className="block text-xs capitalize text-muted-foreground">
                  {datumInJaar(n.datum)}
                  {n.wedstrijd ? ` · ${n.wedstrijd}` : ""}
                </span>
                <span className="mt-0.5 line-clamp-3 font-semibold leading-snug text-white">
                  {n.titel}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Team ----------------------------------------------------------------

function TeamInfo({ team }: { team: TeamDetail }) {
  const links = [
    team.links.website
      ? { icon: ExternalLink, label: "Website", href: team.links.website }
      : null,
    team.links.facebook ? { icon: Globe, label: "Facebook", href: team.links.facebook } : null,
    team.links.instagram
      ? { icon: AtSign, label: "Instagram", href: team.links.instagram }
      : null,
  ].filter((l): l is NonNullable<typeof l> => l !== null);

  // Het paspoort komt als kant-en-klare regels van de server: Farm Stock legt
  // de motor vast, Grand National de aandrijflijn en de beplating.
  const specs = team.paspoort;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-black uppercase tracking-wide text-white">
          <Gauge className="size-4 text-primary" aria-hidden />
          De machine
        </h2>
        {specs.length > 0 ? (
          <dl className="overflow-hidden rounded-xl border border-border bg-card">
            {specs.map((s, i) => (
              <div
                key={s.label}
                className={cn(
                  "flex items-center justify-between gap-3 px-4 py-2.5",
                  i > 0 && "border-t border-white/5",
                )}
              >
                <dt className="text-sm text-muted-foreground">{s.label}</dt>
                <dd className="font-semibold text-white">{s.waarde}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="rounded-xl border border-dashed border-white/15 px-4 py-5 text-sm text-muted-foreground">
            Het machinepaspoort van dit team is in TRS nog niet ingevuld.
          </p>
        )}
      </div>

      {/* Alleen Grand National: Farm Stock kent geen teamleden in TRS, en een
          leeg blok zou suggereren dat het team niemand heeft. */}
      {team.sectie === "gn" && (
        <div>
          <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-black uppercase tracking-wide text-white">
            <Users className="size-4 text-primary" aria-hidden />
            Het team
          </h2>
          {team.teamleden.length > 0 ? (
            <ul className="overflow-hidden rounded-xl border border-border bg-card">
              {team.teamleden.map((naam, i) => (
                <li
                  key={naam}
                  className={cn("px-4 py-2.5 font-semibold text-white", i > 0 && "border-t border-white/5")}
                >
                  {naam}
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-xl border border-dashed border-white/15 px-4 py-5 text-sm text-muted-foreground">
              Er staan voor dit team nog geen teamleden in TRS.
            </p>
          )}
        </div>
      )}

      <div>
        <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-black uppercase tracking-wide text-white">
          <ExternalLink className="size-4 text-primary" aria-hidden />
          Het team online
        </h2>
        {links.length > 0 ? (
          <ul className="space-y-2">
            {links.map((l) => (
              <li key={l.label}>
                <a
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 transition-colors active:bg-white/5"
                >
                  <l.icon className="size-4 shrink-0 text-primary" aria-hidden />
                  <span className="min-w-0 flex-1 truncate font-semibold text-white">
                    {l.label}
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-white/30" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-xl border border-dashed border-white/15 px-4 py-5 text-sm text-muted-foreground">
            {team.sectie === "fs"
              ? "Dit team heeft nog geen website of sociale media in TRS staan. Voor Farm Stock bestaan die velden er nog niet; de bedoeling is dat deelnemers dat later zelf kunnen invullen."
              : "Dit team heeft nog geen website of sociale media in TRS staan. De bedoeling is dat deelnemers dat later zelf kunnen invullen."}
          </p>
        )}
      </div>
    </div>
  );
}
